import {booleanQueryParam} from '~/utils/booleanQueryParam';
import {isoWindow} from '~/utils/window';

export const AUTH_PATH_CORE = '/services/login_with_shop/authorize';

interface BuildDiscountAuthorizeUrlParams {
  /** Links analytics events together in one flow. */
  analyticsTraceId: string;
  /** The app's API key from the partners dashboard. */
  apiKey: string;
  devMode?: boolean;
  /** The version/variant of the flow. Used for analytics purposes. */
  flowVersion: string;
  /**
   * Phone capture flag. If `true`, the component is configured to ask for phone consnt.
   * Otherwise, the component is configured to ask for email consent only.
   */
  phoneCapture?: boolean;
  /** The domain of the storefront page that the component is rendered on. */
  storefrontOrigin?: string;
  /**
   * The step at which the discount should be saved. We care mostly about when the value is "never"
   * since not all clients are allowed to use it.
   */
  saveDiscountAt?: string;
  /**
   * Used to force the user to sign in without utilizing any existing sessions. This is needed to
   * support our iframes since they remount and reload when they change parent containers.
   */
  requireVerification: boolean;
}

export const buildDiscountAuthorizeUrl = ({
  analyticsTraceId,
  apiKey,
  devMode,
  flowVersion,
  phoneCapture,
  saveDiscountAt,
  storefrontOrigin,
  requireVerification,
}: BuildDiscountAuthorizeUrlParams): string => {
  /* eslint-disable @typescript-eslint/naming-convention */
  const params = new URLSearchParams({
    analytics_trace_id: analyticsTraceId,
    api_key: apiKey,
    compact_layout: 'true',
    ...(booleanQueryParam(devMode) === 'true' && {dev_mode: 'true'}),
    flow: 'discount',
    flow_version: flowVersion,
    // eslint-disable-next-line no-process-env
    locale: process.env.BUILD_LOCALE || 'en',
    ...(booleanQueryParam(phoneCapture) === 'true' && {phone_capture: 'true'}),
    ...(saveDiscountAt && {save_discount_at: saveDiscountAt}),
    target_origin: isoWindow.location.origin,
    require_verification: requireVerification ? 'true' : 'false',
  });
  /* eslint-enable @typescript-eslint/naming-convention */

  const authDomainCore = storefrontOrigin || isoWindow.location.origin;
  return `${authDomainCore}${AUTH_PATH_CORE}?${params}`;
};
